import {
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  QueryFilter,
} from "@/types/campaign";
import { formatListValues } from "@/utils/sidebar";
import { LaptopMinimal } from "lucide-react";
import { match, P } from "ts-pattern";
import { getFilterByFilterName, type FilterCategory } from "../config";
import { createFilterGroup, filterGroupTypes } from "../filter-types";

type TechnologyConfig = {
  technologyFilter: string;
  hasFilter?: string;
};

function createTechnologyDisplayValue({
  technologyFilter,
  hasFilter,
}: TechnologyConfig) {
  return ({
    filters,
  }: {
    filters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[];
  }) => {
    const baseFilter = hasFilter
      ? getFilterByFilterName(hasFilter, filters)
      : null;
    const technologyFilterData = getFilterByFilterName(
      technologyFilter,
      filters,
    );

    return match({
      hasTechnology: hasFilter ? !!baseFilter : !!technologyFilterData,
      technologies:
        (technologyFilterData && technologyFilterData.values) ||
        (hasFilter && !!baseFilter ? [] : null),
      operator: technologyFilterData?.operator,
    })
      .with({ hasTechnology: true, technologies: [] }, () => "Ja")
      .with(
        { technologies: P.array(P.string) },
        ({ technologies, operator }) => {
          const formattedTechnologies = formatListValues(technologies);
          return operator === "ListContains"
            ? `Skal være ${formattedTechnologies}`
            : `Skal ikke være ${formattedTechnologies}`;
        },
      )
      .otherwise(() => "Ikke angivet");
  };
}

export const technologiesFilterCategory = {
  title: "Teknologier",
  icon: LaptopMinimal,
  description: "Virksomhedernes anvendte teknologier",
  id: "technologies",
  isAdvancedFilter: true,
  filters: {
    liveChat: createFilterGroup({
      id: "live-chat",
      title: "Live chat",
      keywords: ["widget", "support", "kundeservice", "hjælp", "kommunikation"],
      description:
        "Viser om virksomhederne benytter live chat baseret på website-teknologi.",
      filterType: filterGroupTypes.technology,
      checkboxLabel: "Skal have live chat",
      technologyDescription:
        "Inkluder eller fravælg specifikke live chat-teknologier.",
      category: "live chat",
      filters: {
        hasTechnology: {
          fieldName: "Tech.Technologies.Category",
          filterName: "tech-has-live-chat",
        },
        technology: {
          fieldName: "Tech.Technologies.Technology",
          filterName: "tech-live-chat",
        },
      },
      DisplayValue: createTechnologyDisplayValue({
        hasFilter: "tech-has-live-chat",
        technologyFilter: "tech-live-chat",
      }),
    }),
    digitalMarketing: createFilterGroup({
      id: "digital-marketing",
      title: "Digital marketing",
      keywords: [
        "adwords",
        "annoncering",
        "tracking",
        "kampagner",
        "facebook",
        "google",
      ],
      description:
        "Viser om virksomhederne benytter digital markedsføring baseret på website-teknologi.",
      filterType: filterGroupTypes.technology,
      checkboxLabel: "Skal have digital marketing",
      technologyDescription:
        "Inkluder eller fravælg specifikke digital marketing-teknologier.",
      category: "digital marketing",
      filters: {
        hasTechnology: {
          fieldName: "Tech.Technologies.Category",
          filterName: "tech-has-digital-marketing",
        },
        technology: {
          fieldName: "Tech.Technologies.Technology",
          filterName: "tech-digital-marketing",
        },
      },
      DisplayValue: createTechnologyDisplayValue({
        hasFilter: "tech-has-digital-marketing",
        technologyFilter: "tech-digital-marketing",
        // hasTechnologyKey: "hasMarketing",
      }),
    }),
    hasCrm: createFilterGroup({
      id: "crm",
      title: "CRM-system",
      keywords: ["kundehåndtering", "salg", "support"],
      description:
        "Viser om virksomhederne bruger et CRM-system baseret på website-teknologi.",
      filterType: filterGroupTypes.technology,
      filters: {
        hasTechnology: {
          fieldName: "Tech.Technologies.Category",
          filterName: "tech-has-crm",
        },
        technology: {
          fieldName: "Tech.Technologies.Technology",
          filterName: "tech-crm",
        },
      },
      checkboxLabel: "Skal have CRM-system",
      technologyDescription:
        "Inkluder eller fravælg specifikke CRM-teknologier",
      category: "crm",
      DisplayValue: createTechnologyDisplayValue({
        hasFilter: "tech-has-crm",
        technologyFilter: "tech-crm",
      }),
    }),
    eCommerce: createFilterGroup({
      id: "e-commerce",
      title: "E-commerce",
      keywords: ["webshop", "butik", "salg", "handel", "betaling"],
      description: "Viser om virksomhederne har en webshop.",
      filterType: filterGroupTypes.technology,
      checkboxLabel: "Skal have e-commerce",
      technologyDescription:
        "Inkluder eller fravælg specifikke e-commerce teknologier.",
      category: "ecommerce",
      filters: {
        hasTechnology: {
          fieldName: "Tech.Technologies.Category",
          filterName: "tech-has-ecommerce",
        },
        technology: {
          fieldName: "Tech.Technologies.Technology",
          filterName: "tech-ecommerce",
        },
      },
      DisplayValue: createTechnologyDisplayValue({
        hasFilter: "tech-has-ecommerce",
        technologyFilter: "tech-ecommerce",
      }),
    }),
    hasCms: createFilterGroup({
      id: "cms",
      title: "CMS",
      keywords: ["content management system", "hjemmeside", "website"],
      description:
        "Viser om virksomhederne bruger et Content Management System (CMS) baseret på website-teknologi.",
      filterType: filterGroupTypes.technology,
      checkboxLabel: "Skal have CMS",
      technologyDescription:
        "Inkluder eller fravælg specifikke CMS-teknologier. ",
      category: "cms",
      filters: {
        hasTechnology: {
          fieldName: "Tech.Technologies.Category",
          filterName: "tech-has-cms",
        },
        technology: {
          fieldName: "Tech.Technologies.Technology",
          filterName: "tech-cms",
        },
      },
      DisplayValue: createTechnologyDisplayValue({
        hasFilter: "tech-has-cms",
        technologyFilter: "tech-cms",
      }),
    }),
    otherTechnologies: createFilterGroup({
      id: "other-technologies",
      title: "Andre teknologier",
      keywords: ["analyser", "chat", "crm", "e-commerce", "marketing", "cms"],
      description:
        "Find virksomheder som bruger, eller ikke bruger, én eller flere teknologier.",
      filterType: filterGroupTypes.technology,
      checkboxLabel: "Skal have andre teknologier",
      technologyDescription:
        "Inkluder eller fravælg specifikke teknologier uden for de andre kategorier.",
      category: "other",
      filters: {
        hasTechnology: null,
        technology: {
          fieldName: "Tech.Technologies.Technology",
          filterName: "tech-other-technologies",
        },
      },
      DisplayValue: createTechnologyDisplayValue({
        technologyFilter: "tech-other-technologies",
      }),
    }),
  },
} satisfies FilterCategory;
